<template>
  <component
    :is="plain ? 'div' : GlassCard"
    class="profile-card"
    :class="[`profile-card--${layout}`, { 'profile-card--plain': plain, 'profile-card--clickable': clickable }]"
    v-bind="plain ? {} : { padding: layout === 'compact' || layout === 'summary' ? 'sm' : 'md', interactive: clickable, radius: 'xl' }"
    @click="onCardClick"
  >
    <component
      :is="avatarButton ? 'button' : 'div'"
      class="profile-card__avatar-wrap"
      :class="{ 'profile-card__avatar-wrap--button': avatarButton }"
      v-bind="avatarButton ? { type: 'button', 'aria-label': 'Сменить фото профиля' } : {}"
      @click="onAvatarClick"
    >
      <PlayerAvatar
        class="profile-card__avatar"
        :src="player && player.avatarUrl"
        :name="player ? player.playerName : ''"
        :size="avatarPx"
        :shape="avatarShape"
        :corners="avatarCorners"
        :ring="avatarShape === 'circle'"
      />
      <span v-if="avatarButton" class="profile-card__avatar-badge" aria-hidden="true">
        <AppIcon name="camera" :size="layout === 'compact' ? 16 : 18" :stroke-width="2.2" />
      </span>
    </component>
    <div class="profile-card__body">
      <div class="profile-card__name-row">
        <span v-if="player" class="profile-card__name cb-ellipsis">{{ player.playerName }}</span>
        <span v-else class="profile-card__name-skeleton cb-skeleton"></span>
        <button
          v-if="editable && player"
          type="button"
          class="profile-card__edit"
          aria-label="Изменить имя"
          @click.stop="emit('edit')"
        >
          <AppIcon name="pencil" :size="layout === 'compact' ? 18 : 22" />
        </button>
      </div>
      <PlayerStats
        v-if="player || layout !== 'summary'"
        class="profile-card__stats"
        :wins="player ? player.countWins : 0"
        :losses="player ? player.countLosses : 0"
        :inline="layout === 'summary'"
      />
      <span v-else class="profile-card__stats-skeleton cb-skeleton" aria-hidden="true"></span>
    </div>
    <AppIcon v-if="layout === 'summary' && clickable" name="chevron-right" :size="18" class="profile-card__chevron" />
  </component>
</template>

<script setup>
/**
 * ProfileCard — карточка профиля: аватар, имя + карандаш, «Побед | Поражений».
 *
 * <ProfileCard :player="session.me" editable clickable @open="router.push('/profile')" @edit="openRename" />  // экран 1
 * <ProfileCard :player="session.me" layout="compact" plain />                                                // экран 9
 * <ProfileCard :player="session.me" avatar-shape="rounded" avatar-corners />                                 // экран 8
 * <ProfileCard :player="session.me" editable avatar-editable @edit="openRename" @edit-avatar="openAvatar" />  // экран 10
 *
 * props: player (PlayerInfo|null → скелетон), editable (карандаш, emit 'edit'),
 *        avatarEditable (аватар — кнопка со значком камеры, emit 'edit-avatar'),
 *        clickable (вся карточка нажимается, emit 'open'), layout ('row'|'compact'|'summary'),
 *        plain (без фона карточки), avatarShape ('circle'|'rounded'), avatarCorners, avatarSize (px).
 * emits: edit, edit-avatar, open
 */
import { computed } from "vue";
import GlassCard from "./GlassCard.vue";
import PlayerAvatar from "./PlayerAvatar.vue";
import PlayerStats from "./PlayerStats.vue";
import AppIcon from "./AppIcon.vue";

const props = defineProps({
  player: { type: Object, default: null },
  editable: { type: Boolean, default: false },
  avatarEditable: { type: Boolean, default: false },
  clickable: { type: Boolean, default: false },
  layout: { type: String, default: "row", validator: (v) => ["row", "compact", "summary"].includes(v) },
  plain: { type: Boolean, default: false },
  avatarShape: { type: String, default: "circle" },
  avatarCorners: { type: Boolean, default: false },
  avatarSize: { type: Number, default: null },
});

const emit = defineEmits(["edit", "edit-avatar", "open"]);

const avatarPx = computed(() => props.avatarSize || (props.layout === "summary" ? 52 : props.layout === "compact" ? 96 : 112));
const avatarButton = computed(() => props.avatarEditable && !!props.player);

function onAvatarClick(event) {
  if (!avatarButton.value) return;
  event.stopPropagation();
  emit("edit-avatar");
}

function onCardClick() {
  if (props.clickable) emit("open");
}
</script>

<style scoped>
.profile-card {
  display: flex;
  align-items: center;
  gap: 18px;
  min-width: 0;
}

.profile-card--plain {
  padding: 4px 4px;
}

.profile-card--clickable {
  cursor: pointer;
}

.profile-card__avatar-wrap {
  position: relative;
  flex-shrink: 0;
  display: block;
}

.profile-card__avatar-wrap--button {
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: inherit;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

.profile-card__avatar-wrap--button:active {
  transform: scale(0.97);
}

.profile-card__avatar-wrap--button:focus-visible {
  outline: 2px solid var(--cb-lime);
  outline-offset: 4px;
}

.profile-card__avatar-badge {
  position: absolute;
  right: -2px;
  bottom: -2px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  border: 3px solid var(--cb-surface);
  background: var(--cb-lime);
  color: var(--cb-on-lime);
  box-shadow: var(--cb-glow-sm);
}

.profile-card--compact .profile-card__avatar-badge {
  width: 30px;
  height: 30px;
}

.profile-card__body {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.profile-card__name-row {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.profile-card__name {
  font-size: 26px;
  font-weight: 700;
  line-height: 1.1;
  color: var(--cb-text);
}

.profile-card--compact .profile-card__name {
  font-size: 22px;
}

.profile-card__name-skeleton {
  display: block;
  width: 60%;
  height: 24px;
}

.profile-card__edit {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  margin: -4px 0;
  padding: 0;
  border: 0;
  border-radius: 10px;
  background: transparent;
  color: var(--cb-lime);
  cursor: pointer;
}

.profile-card__edit:active {
  background: var(--cb-lime-soft);
}

@media (max-width: 380px) {
  .profile-card {
    gap: 12px;
  }

  .profile-card:not(.profile-card--summary) .profile-card__avatar {
    width: 88px !important;
    height: 88px !important;
  }

  .profile-card__name {
    font-size: 22px;
  }

  .profile-card__stats {
    gap: 10px;
  }
}
.profile-card--summary { gap: 12px; min-height: 76px; }
.profile-card--summary .profile-card__body { gap: 5px; }
.profile-card--summary .profile-card__name-row { gap: 5px; }
.profile-card--summary .profile-card__name { flex: 0 1 auto; min-width: 0; font-size: 17px; line-height: 1.25; }
.profile-card--summary .profile-card__edit { width: 44px; height: 44px; margin-block: -8px; }
.profile-card--summary .profile-card__edit .app-icon { width: 17px; height: 17px; }
.profile-card--summary .profile-card__name-skeleton { height: 19px; }
.profile-card__stats-skeleton { display: block; width: 85%; height: 16.2px; border-radius: 4px; }
.profile-card__chevron { color: var(--cb-text-2); }
</style>
